import { useToast } from '@/components/toast';
import { useCallback, useRef, useState } from 'react';
import { ALLOWED_IMAGE_EXTENSIONS, ALLOWED_IMAGE_TYPES, isImageUrl } from './utils';

// 类型定义
interface UseFileValidationOptions {
  minPixel?: number;
  maxPixel?: number;
}

interface UseOssUploadOptions {
  onUploadComplete?: (url: string) => void;
  onUploadError?: (error: Error) => void;
}

interface UseDragAndDropOptions {
  onFileDrop: (files: File[]) => void;
  onUrlDrop?: (url: string) => Promise<{ type: 'single'; file: File } | null>;
  multiple?: boolean;
}

interface UseImageLoaderOptions {
  onLoad?: (result: { dataUrl: string; width: number; height: number }) => void;
  onError?: (error: string) => void;
}

// 文件验证hook
export const useFileValidation = ({ minPixel = 0, maxPixel = Infinity }: UseFileValidationOptions = {}) => {
  const { showToast } = useToast();

  const isValidImageFile = useCallback(
    (file: File): boolean => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        const fileName = file.name.toLowerCase();
        const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext));

        if (!hasValidExtension) {
          showToast('image.error.invalid_format', 'error');
          return false;
        }
      }
      return true;
    },
    [showToast],
  );

  const validateImageDimensions = useCallback(
    (width: number, height: number): boolean => {
      const imagePixel = width * height;

      if (minPixel > 0 && imagePixel < minPixel) {
        showToast('image.error.dimension_small', 'error', { size: minPixel });
        throw new Error('image.error.dimension_small');
      }

      if (maxPixel < Infinity && imagePixel > maxPixel) {
        return false;
      }

      return true;
    },
    [minPixel, maxPixel, showToast],
  );

  return { isValidImageFile, validateImageDimensions };
};

// OSS上传hook
export const useOssUpload = ({ onUploadComplete, onUploadError }: UseOssUploadOptions = {}) => {
  const [uploading, setUploading] = useState(false);
  const { showToast } = useToast();
  const ossClientRef = useRef<{ current: any }>({ current: null });
  const loadingPromiseRef = useRef<Promise<any> | null>(null);

  // 预加载 OSS 客户端
  const preloadOssClient = useCallback(async () => {
    if (ossClientRef.current.current) return ossClientRef.current.current;

    if (loadingPromiseRef.current) {
      return loadingPromiseRef.current;
    }

    loadingPromiseRef.current = import('@/client/oss-client').then(({ OssClient }) => {
      const client = new OssClient();
      ossClientRef.current.current = client;
      loadingPromiseRef.current = null;
      return client;
    });

    return loadingPromiseRef.current;
  }, []);

  const uploadFile = useCallback(
    async (file: File, _metadata?: { width?: number; height?: number }): Promise<string> => {
      try {
        setUploading(true);

        // 获取或创建 OSS 客户端
        const client = await preloadOssClient();
        const result = await client.upload(file);

        if (result.url) {
          onUploadComplete?.(result.url);
          showToast('image.upload.success', 'success');
          return result.url;
        } else {
          showToast('image.upload.error.no_url', 'error');
          throw new Error('上传失败：未返回URL');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '未知错误';
        if (error instanceof Error) {
          showToast('image.upload.error.failed', 'error');
        } else {
          showToast('image.upload.error.unknown', 'error');
        }
        onUploadError?.(error instanceof Error ? error : new Error(errorMessage));
        throw error;
      } finally {
        setUploading(false);
      }
    },
    [onUploadComplete, onUploadError, showToast, preloadOssClient],
  );

  return { uploadFile, uploading, preloadOssClient };
};

// 拖拽处理hook
export const useDragAndDrop = ({ onFileDrop, onUrlDrop, multiple = false }: UseDragAndDropOptions) => {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragOver(false);

      const { files, items } = event.dataTransfer;

      // 优先处理文件拖拽
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        onFileDrop(multiple ? fileArray : [fileArray[0]]);
        return;
      }

      // 处理网页图片拖拽
      if (items && onUrlDrop) {
        for (const item of Array.from(items)) {
          if (item.type === 'text/uri-list') {
            const url = await new Promise<string>((resolve) => {
              item.getAsString(resolve);
            });

            if (url && isImageUrl(url)) {
              const result = await onUrlDrop(url);

              if (result && result.type === 'single') {
                onFileDrop([result.file]);
              }
              return;
            }
          }
        }
      }
    },
    [onFileDrop, onUrlDrop, multiple],
  );

  return { dragOver, handleDragOver, handleDragLeave, handleDrop };
};

// 粘贴处理hook
export const usePasteHandler = ({ onFilePaste }: { onFilePaste: (file: File) => void }) => {
  const { showToast } = useToast();

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (items) {
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              // 验证文件类型
              if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                const fileName = file.name.toLowerCase();
                const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext));

                if (!hasValidExtension) {
                  showToast('image.error.invalid_format', 'error');
                  return;
                }
              }

              onFilePaste(file);
              return;
            }
          }
        }
      }
    },
    [onFilePaste, showToast],
  );

  return { handlePaste };
};

// 图片加载hook
export const useImageLoader = ({ onLoad, onError }: UseImageLoaderOptions = {}) => {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const loadImageFromUrl = useCallback(
    async (url: string): Promise<{ width: number; height: number }> => {
      return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };

        img.onerror = () => {
          const error = 'Failed to load image from URL';
          onError?.(error);
          showToast('image.load.error.from_url', 'error');
          reject(new Error(error));
        };

        img.src = url;
      });
    },
    [onError, showToast],
  );

  const loadImageFromFile = useCallback(
    async (file: File): Promise<{ dataUrl: string; width: number; height: number }> => {
      return new Promise((resolve, reject) => {
        setLoading(true);
        const reader = new FileReader();

        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          const img = new Image();

          img.onload = () => {
            const result = { dataUrl, width: img.width, height: img.height };
            onLoad?.(result);
            setLoading(false);
            resolve(result);
          };

          img.onerror = () => {
            const error = 'Failed to load image';
            onError?.(error);
            showToast('image.load.error.general', 'error');
            setLoading(false);
            reject(new Error(error));
          };

          img.src = dataUrl;
        };

        reader.onerror = () => {
          const error = '文件读取失败';
          onError?.(error);
          showToast('image.file.error.read_failed', 'error');
          setLoading(false);
          reject(new Error(error));
        };

        reader.readAsDataURL(file);
      });
    },
    [onLoad, onError, showToast],
  );

  // 错误类型识别
  const identifyErrorType = useCallback((error: Error) => {
    const message = error.message.toLowerCase();

    // CORS错误特征
    if (
      message.includes('cors') ||
      message.includes('cross-origin') ||
      message.includes('access-control-allow-origin') ||
      message.includes('blocked by cors policy')
    ) {
      return 'cors';
    }

    // 网络错误特征
    if (
      message.includes('failed to fetch') ||
      message.includes('network error') ||
      message.includes('net::err') ||
      message.includes('fetch')
    ) {
      return 'network';
    }

    // HTTP状态错误
    if (message.includes('http') && /\d{3}/.test(message)) {
      return 'http_error';
    }

    // 文件类型错误
    if (message.includes('not an image')) {
      return 'not_image';
    }

    return 'unknown';
  }, []);

  // 获取针对性错误消息的 tolgee key
  const getErrorMessageKey = useCallback((errorType: string) => {
    switch (errorType) {
      case 'cors':
        return 'image.error.cors_blocked';
      case 'network':
        return 'image.error.network_failed';
      case 'http_error':
        return 'image.error.server_error';
      case 'not_image':
        return 'image.validation.not_image';
      default:
        return 'image.load.error.url_failed';
    }
  }, []);

  const handleImageUrl = useCallback(
    async (url: string): Promise<File> => {
      try {
        setLoading(true);

        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) {
          throw new Error('Not an image');
        }

        const filename = url.split('/').pop()?.split('?')[0] || 'image';
        const file = new File([blob], filename, { type: blob.type });

        return file;
      } catch (error) {
        const errorType = identifyErrorType(error as Error);
        const errorMessageKey = getErrorMessageKey(errorType);

        onError?.(error instanceof Error ? error.message : 'Unknown error');
        showToast(errorMessageKey, 'error');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [onError, showToast, identifyErrorType, getErrorMessageKey],
  );

  return { loadImageFromUrl, loadImageFromFile, handleImageUrl, loading };
};
